import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ModalController, Platform, IonicModule, MenuController } from '@ionic/angular';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Capacitor } from '@capacitor/core';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AlertService } from './services/alert/alert.service';
import { AuthService } from './services/authentication/auth.service';
import { DataService } from './services/device/local/data.service';
import { LicenseService } from './services/firebase/license/license.service';
import { PushService } from './services/firebase/notifications/push.service';
import { UserService } from './services/firebase/users/user.service';
import { Geolocation } from '@capacitor/geolocation';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { ChatService } from './services/firebase/chat/chat.service';
import { AppVersion } from './interfaces/settings';
import { ConsoleViewPage } from './private/shared/console-view/console-view.page';
import { DeviceService } from './services/device/info/device.service';
import { NgIf, NgFor } from '@angular/common';
import { HasRoleDirective } from './directives/has-role.directive';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    imports: [IonicModule, NgIf, HasRoleDirective, NgFor]
})
export class AppComponent implements OnDestroy {
  private subscriptions = new Subscription();
  private destroy$ = new Subject<void>();
  refreshStatus: boolean;
  captainName: string;
  badgeText: string;
  userDisplayName: string;
  private currentUserData: any;
  profileImg = null;
  license: boolean;
  forceApp: boolean;
  menuType = 'push';
  chatUsers = [];
  chatId;
  showUsers: boolean;
  msgNotifications: number;
  apView = true;
  currentVersion: AppVersion;
  isDev = false;
  currentApplicationVersion: string;

  constructor(
    private route: Router,
    private auth: AuthService,
    private device: DeviceService,
    private alert: AlertService,
    private alertCon: AlertController,
    private localData: DataService,
    private push: PushService,
    private user: UserService,
    private version: LicenseService,
    private platform: Platform,
    private chat: ChatService,
    private alertC: AlertController,
    private viewConsole: ModalController,
    private iab: InAppBrowser,
    private menuCtrl: MenuController
  ) {
    if (!environment.production) {
      this.setupConsoleInterception();
    }
  }

  private setupConsoleInterception() {
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;
    const localData = this.localData;

    const safeStringify = (obj: any): any => {
      try {
        return JSON.parse(JSON.stringify(obj));
      } catch {
        return String(obj);
      }
    };

    const getObjectPreview = (obj: any): string => {
      try {
        if (obj instanceof Error) {
          return `Error: ${obj.message}`;
        }
        if (obj.constructor && obj.constructor.name !== 'Object') {
          return obj.constructor.name;
        }
        const keys = Object.keys(obj);
        if (keys.length === 0) {return '{}';}
        return `{${keys.slice(0, 2).join(', ')}${keys.length > 2 ? '...' : ''}}`;
      } catch {
        return '{...}';
      }
    };

    const processArg = (arg: any) => {
      if (arg === null) {return { type: 'primitive', value: null, display: 'null' };}
      if (arg === undefined) {return { type: 'primitive', value: undefined, display: 'undefined' };}
      if (typeof arg === 'string') {return { type: 'primitive', value: arg, display: arg };}
      if (typeof arg === 'number' || typeof arg === 'boolean') {
        return { type: 'primitive', value: arg, display: String(arg) };
      }
      if (Array.isArray(arg)) {
        return { type: 'array', value: safeStringify(arg), display: `Array(${arg.length})` };
      }
      if (typeof arg === 'object') {
        return { type: 'object', value: safeStringify(arg), display: getObjectPreview(arg) };
      }
      return { type: 'primitive', value: arg, display: String(arg) };
    };

    const createLogEntry = (type: string, args: any[]) => ({
        type: type,
        args: args.map(processArg)
      });

    console.log = function (...args) {
      originalLog.apply(console, args);
      localData.addLog(createLogEntry('LOG', args));
    };

    console.warn = function (...args) {
      originalWarn.apply(console, args);
      localData.addLog(createLogEntry('WARN', args));
    };

    console.error = function (...args) {
      originalError.apply(console, args);
      localData.addLog(createLogEntry('ERROR', args));
    };
  }

  async ngOnInit() {
    this.initializeUserDisplayName();
    this.lockToPortrait();
    this.setupAuthState();
    this.initializeVersion();

    this.initializeChatUsers();
    this.setupChatUserRefresh();
  }

  private initializeUserDisplayName(): void {
    const displayNameSub = this.auth.userData
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user?.display) {
          this.userDisplayName = user.display;
          this.currentUserData = user;
          this.updateBadgeFromUser(user);
        }
      });
    this.subscriptions.add(displayNameSub);
  }

  private updateBadgeFromUser(user: any): void {
    if (!this.captainName || !user) {return;}
    const isAppleUser = this.containsApple(this.captainName);
    this.apView = !isAppleUser;
    this.badgeText = this.apView ? (user?.role || '') : 'Mate';
  }

  private async lockToPortrait(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: 'portrait' });
        console.log('Screen locked to portrait mode');
      } catch (error) {
        console.warn('Could not lock screen orientation:', error);
      }
    }
  }

  // #region ngOnInit Methods

  private initializeVersion() {
    this.currentApplicationVersion = environment.appVersion;
    this.localData.appVersion = environment.appVersion;
    console.log('App Version: ', this.currentApplicationVersion);
  }

  private setupAuthState() {
    // Run license/permission checks once per login. Session restore and
    // auto-login are handled by Firebase persistence + AutoLoginGuard.
    const authSub = this.auth.currentUser
      .pipe(
        distinctUntilChanged((a, b) => a?.uid === b?.uid),
        takeUntil(this.destroy$)
      )
      .subscribe(user => {
        if (user) {
          this.checkLicense();
          this.checkForPermissions();
        }
      });
    this.subscriptions.add(authSub);
  }

  private initializeChatUsers() {
    this.chatUsers = this.localData.chatUsers;
    this.chatId = this.localData.currentChatId;
  }

  private setupChatUserRefresh(): void {
    const chatUserRefreshSub = this.localData.refreshUsers.pipe(takeUntil(this.destroy$)).subscribe(shouldRefresh => {
      this.refreshStatus = shouldRefresh;
      this.updateChatUserList();
    });

    this.subscriptions.add(chatUserRefreshSub);
    this.localData.resetChatUsers();
  }

  private updateChatUserList(): void {
    if (this.refreshStatus) {
      console.log('Refreshing chat users...');
      this.chatUsers = this.removeDuplicateObjects(this.localData.getChatUsers(), 'id');
      this.chatId = this.chatUsers.length > 0 ? this.chatUsers[0].chatId : null;
      this.showUsers = true;
      console.warn('Chat users updated:', this.chatUsers);
    } else {
      this.showUsers = false;
      console.log('Chat users refresh disabled');
    }
  }

  private updateBadgeAndView(userName: string): void {
    this.captainName = userName;
    // If user data is already available, update badge now
    if (this.currentUserData) {
      this.updateBadgeFromUser(this.currentUserData);
    }
  }

  // #endregion

  async checkForUnreadMsg() {
    const myId = this.auth.getUserId();
    if (!myId) {return;}
    console.log('Checking for unread chat messages for', myId);
    const sub = (await this.chat.totalUnreadMessages(myId)).pipe(takeUntil(this.destroy$)).subscribe(res => {
      console.warn('Unread Message Observable Update:', res);
      this.msgNotifications = res.count;
    });
    console.warn('Number of total unread messages for', myId, '=', this.msgNotifications);
    this.subscriptions.add(sub);
    this.listenForMsgs();
  }

  async listenForMsgs() {
    const id = this.auth.getUserId();
    if (!id) {return;}
    const isClient = await this.auth.isAppClient(id);

    if (isClient) {
      console.log('User is a client, not subscribing here for msg updates');
    } else {
      this.msgNotifications = this.localData.unreadMsgNotifcations;
      const msgSub = this.localData.unreadMsg$.pipe(takeUntil(this.destroy$)).subscribe(newMsg => {
        console.log('Listening for msgs, badge count =', this.msgNotifications, 'msgObservable = ', newMsg);
        if (newMsg) {
          this.msgNotifications = this.localData.unreadMsgNotifcations;
          console.log('Updating Badge Count to:', this.msgNotifications);
        }
      });
      this.subscriptions.add(msgSub);
    }
  }

  removeDuplicateObjects<T extends Record<string, any>>(arr: T[], property: keyof T): T[] {
    const uniqueObjects: { [key: string]: T } = {};

    arr.forEach(obj => {
      const key = obj[property];
      if (!(key in uniqueObjects)) {
        uniqueObjects[key] = obj;
      }
    });

    return Object.values(uniqueObjects);
  }

  // #region /////////// Loading Page ///////////

  async checkForPermissions() {
    await Geolocation.checkPermissions()
      .then(() => {
        console.log('User has permission for Geolocation');
      })
      .catch(error => {
        console.log('there was an error', error);
        this.askPermissions();
      });
  }

  async askPermissions() {
    await Geolocation.requestPermissions()
      .then(async request => {
        console.log('This is the request', request);
        if (request.location === 'denied') {
          console.log('[askPermissions] was denied by user', request);
          const alert = await this.alertCon.create({
            header: 'Location Error',
            message: 'Please go into settings and change GPS to While Using the App'
          });
          await alert.present();
        } else {
        }
      })
      .catch(error => {
        console.log('[askPermissions] was denied by user', error);
      });
  }

  async getUserProfile() {
    await this.checkForUnreadMsg();
    if (!this.captainName) {
      console.log('Cannot set badge name, getUserProfile');
      this.updateBadgeAndView(this.userDisplayName);

      this.push.initPush();
      const userProfile = this.user.getUserProfile().pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.profileImg = data;
        if (this.profileImg?.dev) {
          this.isDev = true;
          this.localData.isDeveloper = true;
        }
      });
      this.subscriptions.add(userProfile);
    }
  }

  containsApple(inputString: string): boolean {
    const targetWord = 'Apple';
    const regex = new RegExp(`\b${targetWord}\b`, 'i');
    return regex.test(inputString);
  }

  // #endregion ///////////////////////////////

  // #region ///////// Main Code ///////////////

  async checkLicense() {
    const vers = this.version.checkSubscription().pipe(takeUntil(this.destroy$)).subscribe(res => {
      // docData emits undefined when the doc isn't available yet (e.g. offline cold start);
      // wait for real data instead of crashing or showing a false "inactive license" alert
      if (!res) {
        return;
      }
      this.license = res.active;
      console.log('Is license active? ', this.license);
      if (this.license !== true) {
        const msgTitle = 'Subscription Inactive';
        const msgSub = 'This app license is no longer valid';
        const msgBody = 'Please contact PCS representive to renew license';

        this.alert.inactiveLicense(msgTitle, msgSub, msgBody, false);
      } else {
        this.alert.dismissLicense();
        this.checkForNewVersion();
      }
    });
    this.subscriptions.add(vers);
    this.getUserProfile();
  }

  async checkForNewVersion() {
    const platform = await this.device.getDevicePlatform();
    const force = this.version.checkVersion().pipe(takeUntil(this.destroy$)).subscribe(async res => {
      const isAdmin = this.auth.hasRoles(['admin']);
      this.forceApp = await this.version.forceNewVersion(isAdmin, res.minSupportedVersion);
      console.log('Do we need to force app update?', this.forceApp);
      if (this.forceApp === true) {
        const version = await this.version.getLatestVersion();
        const versionString = `${version.major}.${version.minor}.${version.revision}`;
        const msgTitle = 'Version ' + versionString + ' Available';
        const msgSub = 'This version no longer supported';
        const msgBody = 'Please visit the App Store to update to the newest version';

        await this.alert.newBuildAvailable(msgTitle, msgSub, msgBody, platform, false);
      } else {
        await this.alert.dismissNewBuild();
      }
    });
    this.subscriptions.add(force);
  }

  // #endregion ////////////////////////////////

  // #region ////////// Navigation /////////////

  private navigateTo(path: string) {
    this.route.navigate(['private', path]);
  }

  goToAdmin() {
    this.navigateTo('options');
  }
  goToClients() {
    this.navigateTo('client-list');
  }
  goToAddClients() {
    this.navigateTo('client-add');
  }
  goToDbSettings() {
    this.navigateTo('settings');
  }
  goToAddNewUser() {
    this.navigateTo('users');
  }
  startNewReport() {
    this.navigateTo('select');
  }
  goToProfile() {
    this.navigateTo('profile');
  }
  goToAssignInspections() {
    this.navigateTo('assign');
  }
  goToMyList() {
    this.navigateTo('view');
  }
  goToChats() {
    this.navigateTo('chat-main');
  }
  goToTimeClock() {
    this.navigateTo('timeclock');
  }
  goToTimesheet() {
    this.navigateTo('timesheet');
  }
  goToPayroll() {
    this.navigateTo('payroll');
  }
  goToTimeCards() {
    this.navigateTo('timecards');
  }
  goToBoatProfile() {
    this.navigateTo('boat-profile');
  }
  goToServiceRequests() {
    this.navigateTo('service-requests');
  }
  goToTrainingVideos() {
    this.navigateTo('videos');
  }
  goToPCSHandbook() {
    this.iab.create('https://pcs-docs.netlify.app/', '_blank', {
      toolbarcolor: '#00a6aa',
      closebuttoncolor: '#fff',
      hideurlbar: 'yes'
    });
  }

  async logout() {
    this.ngOnDestroy();
    await this.auth.logout();
  }

  // #endregion ////////////////////////////

  async leaveChat() {
    const event = 'leave';
    const msg = ' has left the group';
    await this.chat.addMessage(this.chatId, msg, event);
    await this.chat.leaveChat(this.chatId);
    this.route.navigateByUrl('/private/chat-main');
  }

  async kickUser(data) {
    console.log('KickUser Data', data);
    console.log(this.chatUsers);
  }

  async backConfirm() {
    const alert = await this.alertC.create({
      header: 'Confirm',
      subHeader: '',
      message: 'Do you want to leave the chat?',
      buttons: [
        {
          text: 'Back'
        },
        {
          text: 'Leave',
          handler: () => {
            this.leaveChat();
          }
        }
      ]
    });

    await alert.present();
  }

  async confirmKick(data) {
    if (this.isDev) {
      const alert = await this.alertC.create({
        header: 'Confirm',
        subHeader: '',
        message: 'Are you sure you want to kick ' + data.user + '?',
        buttons: [
          {
            text: 'Back'
          },
          {
            text: 'Kick',
            handler: () => {
              this.kickUser(data);
            }
          }
        ]
      });

      await alert.present();
    } else {
      console.log('Do nothing');
    }
  }

  get initials(): string {
    if (!this.captainName) { return '?'; }
    const words = this.captainName.replace(/capt\./i, '').trim().split(/\s+/);
    return words.filter(w => w.length > 0).slice(0, 2).map(w => w[0].toUpperCase()).join('');
  }

  isActive(path: string): boolean {
    return this.route.url.includes(path);
  }

  closeMenu() {
    this.menuCtrl.close('menu');
  }

  goToDashboard() {
    this.navigateTo('home');
  }

  async consoleLogs() {
    const modal = await this.viewConsole.create({
      component: ConsoleViewPage
    });
    modal.present();

    await modal.onWillDismiss();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions?.unsubscribe();

    if (Capacitor.isNativePlatform() === true) {
      console.log('this platform is:', this.platform.is('ios'));
      this.push.removeAllListeners();
    }
  }
}
